import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import gsap from 'gsap';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
  element?: HTMLElement;
}

@Component({
  selector: 'glow-pulse',
  templateUrl: './glow-pulse.component.html',
  styleUrl: './glow-pulse.component.scss',
  standalone: true,
  imports: [CommonModule],
})
export class GlowPulseComponent implements AfterViewInit {
  @ViewChild('glowRing') glowRing!: ElementRef<HTMLDivElement>;
  @Input() isActive = false;
  @Input() color = '#f7f7f7';
  @Input() sparkleRadius = 30;
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;
  particles: Particle[] = [];
  sparkleElements: HTMLElement[] = [];
  timelines: gsap.core.Timeline[] = [];

  constructor(private renderer: Renderer2) {}

  ngAfterViewInit() {
    if (this.isActive) {
      this.createParticles();
      this.createSparkles();
    }

    if (this.glowRing) {
      gsap.to(this.glowRing.nativeElement, {
        scale: 1.3,
        opacity: 0.8,
        duration: 1,
        ease: 'power1.inOut',
        yoyo: true,
        repeat: -1, // infinite loop
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isActive']) {
      if (this.isActive) {
        this.createParticles();
        this.createSparkles();
      } else {
        this.clearParticles();
        this.clearSparkles();
      }
    }
  }

  ngOnDestroy() {
    this.clearParticles();
    this.clearSparkles();
  }

  createParticles() {
    this.clearParticles();

    this.particles = [];
    for (let i = 0; i < 12; i++) {
      this.particles.push({
        id: i,
        x: Math.random() * 120 - 60,
        y: Math.random() * 120 - 60,
        size: Math.random() * 4 + 2,
        delay: Math.random() * 2,
        duration: Math.random() * 3 + 2,
      });
    }

    this.particles.forEach((particle) => {
      const div = this.renderer.createElement('div');
      this.renderer.addClass(div, 'particle');
      this.renderer.setStyle(div, 'background-color', this.color);
      this.renderer.setStyle(div, 'box-shadow', `0 0 ${particle.size * 2}px ${this.color}`);
      this.renderer.setStyle(div, 'width', `${particle.size}px`);
      this.renderer.setStyle(div, 'height', `${particle.size}px`);
      this.renderer.setStyle(div, 'left', '50%');
      this.renderer.setStyle(div, 'top', '50%');
      this.renderer.setStyle(div, 'position', 'absolute');
      this.renderer.setStyle(div, 'border-radius', '50%');
      this.renderer.setStyle(div, 'opacity', '0.8');
      this.renderer.appendChild(this.container.nativeElement, div);

      particle.element = div;

      // Animate particle with GSAP timeline
      const tl = gsap.timeline({ repeat: -1, ease: 'easeInOut', delay: particle.delay });
      tl.fromTo(
        div,
        { x: 0, y: 0, scale: 0, opacity: 0 },
        {
          x: particle.x,
          y: particle.y,
          scale: 1,
          opacity: 1,
          duration: particle.duration * 0.4,
        }
      )
        .to(div, {
          scale: 1,
          opacity: 0.8,
          duration: particle.duration * 0.2,
        })
        .to(div, {
          scale: 0,
          opacity: 0,
          x: particle.x,
          y: particle.y,
          duration: particle.duration * 0.4,
        });

      this.timelines.push(tl);
    });
  }

  createSparkles() {
    this.clearSparkles();

    const sparkleCount = 6;

    for (let i = 0; i < sparkleCount; i++) {
      const divWrapper = this.renderer.createElement('div');
      this.renderer.setStyle(divWrapper, 'position', 'absolute');
      this.renderer.setStyle(divWrapper, 'left', '50%');
      this.renderer.setStyle(divWrapper, 'top', '50%');
      this.renderer.setStyle(divWrapper, 'width', '8px');
      this.renderer.setStyle(divWrapper, 'height', '8px');
      this.renderer.setStyle(divWrapper, 'transformOrigin', 'center center');
      this.renderer.appendChild(this.container.nativeElement, divWrapper);

      const sparkle = this.renderer.createElement('div');
      this.renderer.setStyle(sparkle, 'width', '8px');
      this.renderer.setStyle(sparkle, 'height', '8px');
      this.renderer.setStyle(sparkle, 'background', `linear-gradient(45deg, ${this.color}, white)`);
      this.renderer.setStyle(sparkle, 'box-shadow', `0 0 8px ${this.color}`);
      this.renderer.setStyle(sparkle, 'transform', 'rotate(45deg)');
      this.renderer.appendChild(divWrapper, sparkle);

      this.sparkleElements.push(divWrapper);

      // Animate sparkle orbit and scale with GSAP
      const angle = (i * 60 * Math.PI) / 180; // radians
      const tl = gsap.timeline({ repeat: -1, ease: 'easeInOut', delay: i * 0.3 });
      tl.fromTo(
        divWrapper,
        { scale: 0, rotation: 0, x: 0, y: 0 },
        {
          scale: 1,
          rotation: 180,
          x: Math.cos(angle) * this.sparkleRadius,
          y: Math.sin(angle) * this.sparkleRadius,
          duration: 2,
        }
      ).to(divWrapper, {
        scale: 0,
        rotation: 360,
        x: Math.cos(angle) * this.sparkleRadius,
        y: Math.sin(angle) * this.sparkleRadius,
        duration: 2,
      });

      this.timelines.push(tl);
    }
  }

  clearParticles() {
    this.timelines.forEach((tl) => tl.kill());
    this.timelines = [];

    if (!this.container) return;
    this.particles.forEach((particle) => {
      if (particle.element) {
        this.renderer.removeChild(this.container.nativeElement, particle.element);
      }
    });
    this.particles = [];
  }

  clearSparkles() {
    if (!this.container) return;
    this.sparkleElements.forEach((el) => this.renderer.removeChild(this.container.nativeElement, el));
    this.sparkleElements = [];
  }

  get glowRingStyle() {
    return {
      background: 'radial-gradient(circle at center, transparent 60%, ' + this.color + ' 98%, transparent 100%)',
    };
  }
}
